<script setup lang="ts">
import { Message } from '@arco-design/web-vue'

const props = defineProps<{
  visible: boolean
  phone: string
  type?: number // 2=手机号, 3=邮箱
}>()

const emits = defineEmits<{
  (e: 'update:visible', val: boolean): void
  (e: 'success'): void
}>()

const { t } = useI18n()
const { $request } = useNuxtApp()

// 图形验证码数据
const captchaId = ref('')
const imageBase64 = ref('')
const captchaInput = ref('')
const loading = ref(false)
const refreshing = ref(false)
const errorMsg = ref('')

// 获取图形验证码图片
// keepError=true 时不清空 errorMsg（用于验证码错误后刷新图片，保留错误提示）
async function fetchCaptchaImage(keepError = false) {
  refreshing.value = true
  if (!keepError) {
    errorMsg.value = ''
  }
  captchaInput.value = ''
  try {
    const res = await $request<Api.User.CaptchaInfo>('/user/captcha/image', {
      method: 'GET',
    })
    captchaId.value = res.captchaId
    imageBase64.value = res.imageBase64
  }
  catch (error) {
    console.error('获取图形验证码失败:', error)
    errorMsg.value = t('图形验证码加载失败，请重试')
  }
  finally {
    refreshing.value = false
  }
}

// 弹窗打开时自动加载图形验证码
watch(() => props.visible, (val) => {
  if (val) {
    fetchCaptchaImage()
  }
  else {
    // 关闭时重置状态
    captchaInput.value = ''
    errorMsg.value = ''
  }
})

// 确认：校验图形验证码并发送短信/邮件
async function handleConfirm() {
  if (!captchaInput.value.trim()) {
    errorMsg.value = t('请输入图形验证码')
    return
  }
  const url = props.type === 3 ? '/user/email/send' : '/user/phone/sendSms'
  const query = props.type === 3
    ? { email: props.phone, captchaId: captchaId.value, captchaCode: captchaInput.value.trim() }
    : { phone: props.phone, captchaId: captchaId.value, captchaCode: captchaInput.value.trim() }

  loading.value = true
  errorMsg.value = ''
  try {
    await $request(url, { query, isCustomError: true })
    Message.success(t(`验证码发送成功${props.type === 3 ? '，请前往邮箱查看' : ''}`))
    emits('update:visible', false)
    emits('success')
  }
  catch (error: any) {
    const code = error?.code ?? error?.data?.code
    if (code === 40024 || code === 40025) {
      errorMsg.value = code === 40024 ? t('图形验证码已过期，请刷新') : t('图形验证码错误，请重新输入')
      fetchCaptchaImage(true) // keepError=true，保留错误提示
    }
    else {
      console.error('发送验证码失败:', error)
    }
  }
  finally {
    loading.value = false
  }
}

function handleCancel() {
  emits('update:visible', false)
}

function handleRefresh() {
  fetchCaptchaImage()
}

// 按 Enter 键确认
function handleKeydown(e: KeyboardEvent) {
  if (e.key === 'Enter') {
    handleConfirm()
  }
}
</script>

<template>
  <a-modal
    :visible="visible"
    :footer="false"
    :width="360"
    simple
    :mask-closable="false"
    :title="$t('安全验证')"
    modal-class="g-captcha-modal"
    @cancel="handleCancel"
  >
    <div class="captcha-modal-body">
      <p class="captcha-tip">
        {{ $t('请完成图形验证后发送验证码') }}
      </p>

      <!-- 图形验证码图片区域 -->
      <div class="captcha-image-row">
        <div class="captcha-image-wrap">
          <a-spin :loading="refreshing" class="h-full w-full flex items-center justify-center">
            <img
              v-if="imageBase64 && !refreshing"
              :src="imageBase64"
              alt="captcha"
              class="captcha-img"
            >
            <div v-else-if="!refreshing" class="captcha-img-placeholder">
              {{ $t('加载中...') }}
            </div>
          </a-spin>
        </div>
        <button
          class="captcha-refresh-btn"
          :disabled="refreshing"
          type="button"
          @click="handleRefresh"
        >
          <span class="i-carbon-renew captcha-refresh-icon" />
          {{ $t('换一张') }}
        </button>
      </div>

      <!-- 输入框 -->
      <a-input
        v-model="captchaInput"
        :placeholder="$t('请输入图形验证码')"
        :max-length="6"
        allow-clear
        class="captcha-input"
        @input="errorMsg = ''"
        @keydown="handleKeydown"
      />

      <!-- 错误提示 -->
      <p v-if="errorMsg" class="captcha-error">
        {{ errorMsg }}
      </p>

      <!-- 操作按钮 -->
      <div class="captcha-actions">
        <a-button class="captcha-cancel-btn" @click="handleCancel">
          {{ $t('取消') }}
        </a-button>
        <a-button
          type="primary"
          :loading="loading"
          class="captcha-confirm-btn"
          @click="handleConfirm"
        >
          {{ $t('发送验证码') }}
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<style scoped lang="less">
.captcha-modal-body {
  padding: 4px 0 8px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.captcha-tip {
  margin: 0;
  font-size: 14px;
  color: var(--color-text-2);
  text-align: center;
}

.captcha-image-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.captcha-image-wrap {
  flex-shrink: 0;
  width: 160px;
  height: 60px;
  border: 1px solid var(--color-border-1);
  border-radius: 8px;
  overflow: hidden;
  background: var(--color-fill-2);
  display: flex;
  align-items: center;
  justify-content: center;
}

.captcha-img {
  width: 160px;
  height: 60px;
  object-fit: contain;
  display: block;
  cursor: pointer;
}

.captcha-img-placeholder {
  font-size: 12px;
  color: var(--color-text-4);
}

.captcha-refresh-btn {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 12px;
  height: 32px;
  border: 1px solid var(--color-border-1);
  border-radius: 6px;
  background: transparent;
  color: var(--color-brand-6);
  font-size: 13px;
  cursor: pointer;
  transition: opacity 0.2s;
  white-space: nowrap;

  &:hover:not(:disabled) {
    opacity: 0.8;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

.captcha-refresh-icon {
  font-size: 14px;
}

.captcha-input {
  :deep(.arco-input-wrapper) {
    height: 44px;
    border-radius: 8px;
    border: 1px solid var(--color-border-1) !important;
    background: white;

    &:not(.arco-input-disabled):hover {
      border-color: var(--color-brand-6) !important;
    }
  }
}

.captcha-error {
  margin: -8px 0 0;
  font-size: 12px;
  color: var(--color-danger-6, #f53f3f);
}

.captcha-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 4px;
}

.captcha-cancel-btn {
  min-width: 80px;
}

.captcha-confirm-btn {
  min-width: 100px;
  background: linear-gradient(99.82deg, #914DFA 17.49%, #681CF9 91.26%);
  border: none;

  &:hover {
    background: linear-gradient(99.82deg, #7B41D5 17.49%, #5919D4 91.26%);
  }
}
</style>

<style lang="less">
.g-captcha-modal {
  .arco-modal-header {
    padding: 20px 20px 0;
    border-bottom: none;
  }
  .arco-modal-body {
    padding: 12px 20px 20px;
  }
}
</style>